import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

(() => {
  if (window.__manacareProductGalleryClickFix) return;
  window.__manacareProductGalleryClickFix = true;

  function getSlides(gallery) {
    return Array.from(gallery.querySelectorAll('slideshow-slide'));
  }

  function selectSlide(gallery, index) {
    const slides = getSlides(gallery);
    if (!slides.length) return;

    const safeIndex = Math.max(0, Math.min(Number(index) || 0, slides.length - 1));

    slides.forEach((slide, slideIndex) => {
      const selected = slideIndex === safeIndex;
      slide.hidden = !selected;
      slide.setAttribute('aria-hidden', String(!selected));
    });

    gallery.querySelectorAll('.slideshow-control[aria-selected], .slideshow-controls__thumbnail').forEach((control, controlIndex) => {
      control.setAttribute('aria-selected', String(controlIndex === safeIndex));
    });

    const currentCounter = gallery.querySelector('[ref="current"]');
    if (currentCounter) currentCounter.textContent = String(safeIndex + 1);
    gallery.dataset.currentSlide = String(safeIndex);
  }

  function parseAction(control, gallery) {
    const action = control.getAttribute('on:click') || '';
    const match = action.match(/^\/(select|next|previous)(?:\/(-?\d+))?$/);

    if (match) return { name: match[1], index: Number(match[2] || 0) };
    if (control.classList.contains('slideshow-controls__thumbnail')) {
      return {
        name: 'select',
        index: Array.from(gallery.querySelectorAll('.slideshow-controls__thumbnail')).indexOf(control),
      };
    }
    if (control.classList.contains('slideshow-control--next')) return { name: 'next' };
    if (control.classList.contains('slideshow-control--previous')) return { name: 'previous' };
  }

  function initGallery(gallery) {
    if (!gallery || gallery.dataset.manacareGalleryFixed === 'true') return;
    gallery.dataset.manacareGalleryFixed = 'true';
    selectSlide(gallery, gallery.getAttribute('initial-slide') || 0);
  }

  function init() {
    document.querySelectorAll('slideshow-component').forEach(initGallery);
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const control = target?.closest('[on\\:click], .slideshow-controls__thumbnail, .slideshow-control--next, .slideshow-control--previous');
      const gallery = control?.closest('slideshow-component');
      const action = control && gallery ? parseAction(control, gallery) : null;

      if (!action) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      initGallery(gallery);

      if (action.name === 'select') {
        selectSlide(gallery, action.index);
      } else if (action.name === 'next') {
        selectSlide(gallery, (Number(gallery.dataset.currentSlide || 0) + 1) % getSlides(gallery).length);
      } else {
        selectSlide(gallery, (Number(gallery.dataset.currentSlide || 0) - 1 + getSlides(gallery).length) % getSlides(gallery).length);
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});


// Manacare nav patch: OUR STORY menu item, 2026-06-22 21:48 EDT
(function manageManacareHeaderNavFromAsset() {
  function labelOf(item) {
    return (item.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
  }

  function update() {
    var about = document.querySelector('.mana-about');
    if (about && !about.id) about.id = 'our-story';

    var header = document.querySelector('#header-group, header-component');
    if (!header) return;

    header.querySelectorAll('a, button').forEach(function (item) {
      if (labelOf(item) === 'GET MANACARE') {
        var wrapper = item.closest('li, .menu-drawer__list-item, .menu-list__list-item, .menu-list__overflow-item');
        (wrapper || item).remove();
      }
    });

    header.querySelectorAll('a').forEach(function (homeLink) {
      if (labelOf(homeLink) !== 'HOME') return;
      var wrapper = homeLink.closest('li, .menu-drawer__list-item, .menu-list__list-item, .menu-list__overflow-item') || homeLink;
      var container = wrapper.parentElement;
      if (!container || container.querySelector('a[href="/#our-story"], a[href="#our-story"]')) return;

      var item = wrapper.cloneNode(true);
      item.removeAttribute('aria-current');
      item.classList.remove('active');
      var storyLink = item.matches && item.matches('a') ? item : item.querySelector('a');
      if (!storyLink) return;
      storyLink.setAttribute('href', '/#our-story');
      storyLink.removeAttribute('aria-current');
      storyLink.removeAttribute('aria-expanded');
      storyLink.textContent = 'OUR STORY';
      wrapper.insertAdjacentElement('afterend', item);
    });
  }

  update();
  document.addEventListener('DOMContentLoaded', update, { once: true });
  window.addEventListener('load', update, { once: true });
  setTimeout(update, 250);
  setTimeout(update, 1000);
  var header = document.querySelector('#header-group');
  // Legacy self-observing MANACARE header hook removed.
})();

// Manacare header brand-title removal, 2026-06-22 21:56 EDT
(function removeManacareHeaderBrandTitle() {
  function normalized(value) {
    return (value || '').replace(/\s+/g, ' ').trim().toUpperCase();
  }
  function compact(value) {
    return normalized(value).replace(/[^A-Z0-9]/g, '');
  }
  function removeNode(node) {
    if (!node) return;
    var wrapper = node.closest && node.closest('a.header-logo, .header-logo, [data-testid="header-logo"]');
    (wrapper || node).remove();
  }
  function update(root) {
    var scope = root || document;
    scope.querySelectorAll('a, button, .header-logo, [data-testid="header-logo"]').forEach(function (item) {
      var text = item.textContent || '';
      var label = normalized(text);
      var tight = compact(text);
      if (label === 'GET MANACARE' || tight === 'GETMANACARE') {
        var wrapper = item.closest && item.closest('li, .menu-drawer__list-item, .menu-list__list-item, .menu-list__overflow-item, a.header-logo, .header-logo');
        (wrapper || item).remove();
      }
    });
  }
  update();
  document.addEventListener('DOMContentLoaded', function () { update(); }, { once: true });
  window.addEventListener('load', function () { update(); }, { once: true });
  setTimeout(update, 150);
  setTimeout(update, 800);
  var header = document.querySelector('#header-group, header-component');
  // Legacy self-observing MANACARE header hook removed.
})();


// Manacare mobile menu href hardening, 2026-07-01
(function hardenManacareMenuHrefs() {
  var hrefs = {
    'HOME': '/',
    'BECOME A PARTNER': '/#become-a-partner',
    'OUR STORY': '/#our-story',
    'CATALOG': '/collections/all',
    'CONTACT': '/pages/contact'
  };
  function labelOf(item) {
    return (item.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
  }
  function update() {
    document.querySelectorAll('#header-group a, header-component a, .menu-drawer a').forEach(function(link) {
      var href = hrefs[labelOf(link)];
      if (!href) return;
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      link.setAttribute('data-manacare-nav', labelOf(link).toLowerCase().replace(/\s+/g, '-'));
    });
  }
  update();
  document.addEventListener('DOMContentLoaded', update, { once: true });
  window.addEventListener('load', update, { once: true });
  setTimeout(update, 50);
  setTimeout(update, 300);
  setTimeout(update, 1000);
  var header = document.querySelector('#header-group, header-component');
  // Legacy self-observing MANACARE header hook removed.
})();

// Manacare mobile drawer close behavior, 2026-06-30
(function closeManacareMobileDrawerOnTap() {
  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function drawerDetails() {
    return Array.prototype.slice.call(document.querySelectorAll(
      '#Details-menu-drawer-container, details[id*="menu-drawer"], details.menu-drawer-container, header-component details[open], #header-group details[open]'
    )).filter(function(details) {
      return details && details.open && (details.querySelector('.menu-drawer') || details.id === 'Details-menu-drawer-container');
    });
  }

  function closeDrawer() {
    drawerDetails().forEach(function(details) {
      details.open = false;
      details.removeAttribute('open');
      var summary = details.querySelector('summary[aria-expanded]');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('manacare:mobile-menu-closed'));
  }

  function handleOutsideEvent(event) {
    if (!isMobile()) return;
    if (!drawerDetails().length) return;

    var target = event.target;

    var clickedDrawerClose = target.closest && target.closest(
      '.menu-drawer__close-button, button[aria-label="Close"]'
    );
    if (clickedDrawerClose && clickedDrawerClose.closest('.menu-drawer')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    // The theme reuses the drawer <summary> as the X control while open.
    // Our capture-phase fallback previously treated that control as an
    // ordinary click inside the drawer, so the X never reached a close path.
    var clickedDrawerToggle = target.closest && target.closest(
      '#Details-menu-drawer-container > summary, details[id*="menu-drawer"] > summary, details.menu-drawer-container > summary'
    );
    if (clickedDrawerToggle) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    var clickedNavLink = target.closest && target.closest('.menu-drawer a[href]');
    if (clickedNavLink) {
      window.setTimeout(closeDrawer, 40);
      return;
    }

    var drawer = document.querySelector('.menu-drawer');
    var rect = drawer && drawer.getBoundingClientRect();
    var point = event.touches && event.touches[0] ? event.touches[0] : event;
    if (rect && typeof point.clientX === 'number' && (
      point.clientX < rect.left || point.clientX > rect.right || point.clientY < rect.top || point.clientY > rect.bottom
    )) {
      closeDrawer();
      return;
    }

    if (target.closest && target.closest('.menu-drawer__backdrop')) {
      closeDrawer();
      return;
    }

    var clickedDrawer = target.closest && target.closest('.menu-drawer, #Details-menu-drawer-container summary, details[id*="menu-drawer"] summary');
    if (!clickedDrawer) closeDrawer();
  }

  document.addEventListener('click', handleOutsideEvent, true);
  document.addEventListener('touchstart', handleOutsideEvent, true);

  document.addEventListener('keydown', function(event) {
    if (event.key === 'Escape' && isMobile()) closeDrawer();
  });
})();

// Manacare mobile drawer navigation guard, 2026-07-01
(function restoreManacareDrawerNavigation() {
  var lastHandledAt = 0;

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function closeDrawer() {
    Array.prototype.slice.call(document.querySelectorAll(
      '#Details-menu-drawer-container, details[id*="menu-drawer"], details.menu-drawer-container, header-component details[open], #header-group details[open]'
    )).forEach(function(details) {
      if (!details || !details.open) return;
      details.open = false;
      details.removeAttribute('open');
      var summary = details.querySelector('summary[aria-expanded]');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  }

  function targetFromHash(hash) {
    if (!hash || hash === '#') return null;
    var id = decodeURIComponent(hash.slice(1));
    if (!id) return null;
    if (window.CSS && CSS.escape) return document.querySelector('#' + CSS.escape(id));
    return document.getElementById(id);
  }

  function scrollToHash(hash) {
    var target = targetFromHash(hash);
    if (!target) return false;
    var header = document.querySelector('#header-group, header-component, sticky-header');
    var headerHeight = header ? header.getBoundingClientRect().height : 0;
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    if (history.pushState) history.pushState(null, '', hash);
    else window.location.hash = hash;
    return true;
  }

  function goToHref(href) {
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      window.location.href = href;
      return;
    }

    if (url.origin === window.location.origin && url.pathname === window.location.pathname && url.hash) {
      if (scrollToHash(url.hash)) return;
    }

    window.location.href = url.href;
  }

  function handle(event) {
    if (!isMobile()) return;
    var link = event.target && event.target.closest && event.target.closest('.menu-drawer a[href]');
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '?' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;

    var now = Date.now();
    if (event.type === 'click' && now - lastHandledAt < 700) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    lastHandledAt = now;
    closeDrawer();
    window.setTimeout(function() { goToHref(href); }, 80);
  }

  document.addEventListener('touchstart', handle, true);
  document.addEventListener('click', handle, true);
})();

